import React, { ReactNode, FunctionComponent } from 'react'
import { Box } from 'front-end/src/components'
import {
  Portal,
  BoxProps,
  Popover,
  PopoverBody,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  VisuallyHidden,
  PopoverProps
} from '@chakra-ui/react'

export interface TooltipProps {
  text: any
  isBlock?: boolean
  children: ReactNode
  contentClassName?: string
  key?: any
  delay?: number
  disabled?: boolean
  trigger?: 'hover' | 'click'
  popoverProps?: PopoverProps
  dataTest?: string
}

// Tooltip implemented with popover due to https://github.com/chakra-ui/chakra-ui/issues/3993
const Tooltip: FunctionComponent<TooltipProps & BoxProps> = ({
  contentClassName,
  isBlock,
  children,
  text,
  delay,
  disabled,
  trigger = 'hover',
  popoverProps,
  dataTest = 'Tooltip',
  ...other
}) => {
  if (!children || !React.Children.toArray(children).length) {
    return null
  }

  if (disabled || !text) {
    return <>{children}</>
  }

  return (
    <Popover
      variant="tooltip"
      enabled={!disabled}
      closeDelay={delay}
      trigger={trigger}
      {...popoverProps}
      isLazy={true}
    >
      {({ isOpen }) => (
        <>
          <PopoverTrigger>
            <Box
              className="Tooltip"
              as={isBlock ? undefined : 'span'}
              tabIndex={0}
              {...other}
              data-test={dataTest}
            >
              {children}
              {
                // this line adds ability to read tooltip content with hover behaviour, currently it's not a part of WAI-ARIA spec
                trigger === 'hover' ? (
                  <VisuallyHidden>{text}</VisuallyHidden>
                ) : null
              }
            </Box>
          </PopoverTrigger>
          {/*We need to prevent rendering hundreds of portals on pages with hundreds of tooltips (campaigns), it causes performance issues*/}
          {isOpen && (
            <Portal>
              <PopoverContent className={contentClassName}>
                <PopoverArrow />
                <PopoverBody>{text}</PopoverBody>
              </PopoverContent>
            </Portal>
          )}
        </>
      )}
    </Popover>
  )
}

export default Tooltip
